<template>
  <div class="view-revenue">
    <div v-if="loadingRevenue && stripeReady" style="margin-top: 10px" class="text-center">
      <v-skeleton-loader type="table"></v-skeleton-loader>
    </div>

    <!-- Empty state -->
    <div v-else-if="!loadingRevenue && !revenueFiltered" class="d-flex align-center justify-center"
      style="min-height: 70vh">
      <v-row class="d-flex align-center justify-center">
        <v-col class="d-flex flex-column align-center justify-center" cols="12">
          <v-img class="mb-6" height="130" :src="require('@/assets/icons/admin+/financial_revenue.svg')"
            contain></v-img>
          <div class="text-center mb-6">
            <span class="font-roboto text-empty">{{ $t('message.transactions_admin_plus.no_revenue_yet') }}</span>
            <div class="text-body-1 text-empty">
              {{ $t('message.transactions_admin_plus.revenue_will_appear_here') }}
            </div>
          </div>
          <PlusButton small intent="primary" @click="navigateTo({ name: 'products' })">
            <span class="font-roboto text-button d-inline">{{
              $t('message.transactions_admin_plus.go_to_products_memberships') }}</span>
          </PlusButton>
        </v-col>
      </v-row>
    </div>

    <div v-else>
      <v-container fluid>
        <!-- Summary cards -->
        <div v-if="false"
          class="d-inline-flex flex-wrap mb-4 rounded-lg mobile-header-financial justify-space-between align-center pa-lg-2 pa-5">
          <v-col cols="12" lg="auto">
            <div class="d-flex flex-row align-center align-lg-center text-left item_header pb-4">
              <div class="rounded-lg pa-3 mb-2 mr-2 plusLightGreenLighten5">
                <v-img :src="require('@/assets/icons/admin+/atm_local.svg')" width="36" height="36"></v-img>
              </div>
              <div>
                <div class="font-roboto text-h5">
                  <v-progress-circular size="36" v-if="loadingRevenue" :width="2" indeterminate></v-progress-circular>
                  <span v-else>{{ currencySymbol }}{{ monthlyTotalRevenue }}</span>
                </div>
                <div class="text-body-2 font-roboto">{{ $t('message.transactions_admin_plus.monthly_total_revenue') }}
                </div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" lg="auto">
            <div class="d-flex flex-row align-center align-lg-center text-left item_header pb-4">
              <div class="rounded-lg pa-3 mb-2 mr-2 plusTealLighten5">
                <v-img :src="require('@/assets/icons/admin+/currency-exchange.svg')" width="36" height="36"></v-img>
              </div>
              <div>
                <div class="font-roboto text-h5">
                  <v-progress-circular size="20" v-if="loadingRevenue" :width="2" indeterminate></v-progress-circular>
                  <span v-else>{{ currencySymbol }}{{ monthlyRecurringRevenue }}</span>
                </div>
                <div class="text-body-2 font-roboto">{{ $t('message.transactions_admin_plus.monthly_recurring_revenue')
                  }}</div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" lg="auto">
            <div class="d-flex flex-row align-center align-lg-center text-left item_header pb-4">
              <div class="rounded-lg pa-3 mb-2 mr-2 plusAmberLighten5">
                <v-icon size="36" color="plusAmberDarken4">mdi-clock-time-eight-outline</v-icon>
              </div>
              <div>
                <div class="font-roboto text-h5">
                  <v-progress-circular size="20" v-if="loadingRevenue" :width="2" indeterminate></v-progress-circular>
                  <span v-else>{{ oneTimeRevenueCount }}</span>
                </div>
                <div class="text-body-2 font-roboto">{{ $t('message.transactions_admin_plus.one_time_revenue') }}</div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" lg="auto">
            <div class="d-flex flex-row align-center align-lg-center text-left item_header pb-4">
              <div class="rounded-lg pa-3 mb-2 mr-2 plusDeepOrangeLighten5">
                <v-icon size="36" color="plusDeepOrangeAccent4">mdi-block-helper</v-icon>
              </div>
              <div>
                <div class="font-roboto text-h5">
                  <v-progress-circular size="20" v-if="loadingRevenue" :width="2" indeterminate></v-progress-circular>
                  <span v-else>{{ pendingPaymentsCount }}</span>
                </div>
                <div class="text-body-2 font-roboto">{{ $t('message.transactions_admin_plus.pending_payments') }}</div>
              </div>
            </div>
          </v-col>
        </div>

        <!-- Segment selector: Memberships | One-Time -->
        <div class="d-flex justify-center mb-4">
          <v-btn-toggle :value="revenueTypeFilter" @change="$emit('update:revenue-type-filter', $event)" mandatory
            rounded color="primary" dense>
            <v-btn value="subscriptions" small class="px-6 font-roboto text-capitalize">
              <v-icon small class="mr-2">mdi-credit-card-sync-outline</v-icon>
              {{ $t('message.transactions_admin_plus.memberships') }}
            </v-btn>
            <v-btn value="onetime" small class="px-6 font-roboto text-capitalize">
              <v-icon small class="mr-2">mdi-shopping-outline</v-icon>
              {{ $t('message.transactions_admin_plus.one_time_purchases') }}
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- Memberships context widgets: filtered count + monthly income -->
        <v-row v-if="revenueTypeFilter === 'subscriptions'" class="mb-3">
          <v-col cols="12" sm="6">
            <v-card elevation="0" outlined class="text-center rounded-lg pa-2">
              <v-card-subtitle class="text-subtitle-2 pb-0">{{ totalCountTitle }}</v-card-subtitle>
              <v-card-text class="text-h4 pb-2 font-roboto">
                <v-progress-circular size="28" v-if="loadingRevenue" :width="2" indeterminate></v-progress-circular>
                <span v-else>{{ revenueFiltered ? revenueFiltered.length : 0 }}</span>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-card v-bind="attrs" v-on="on" elevation="0" outlined class="text-center rounded-lg pa-2">
                  <v-card-subtitle class="text-subtitle-2 pb-0">
                    {{ totalAmountTitle }}
                    <v-icon class="ml-1" size="15" style="margin-top: -3px">mdi-information-outline</v-icon>
                  </v-card-subtitle>
                  <v-card-text class="text-h4 pb-2 font-roboto">
                    <v-progress-circular size="28" v-if="loadingRevenue" :width="2" indeterminate></v-progress-circular>
                    <span v-else>{{ currencySymbol }}{{ totalRevenueAmount }}</span>
                  </v-card-text>
                </v-card>
              </template>
              <span>{{ totalAmountTooltip }}</span>
            </v-tooltip>
          </v-col>
        </v-row>

        <!-- Filter card -->
        <v-card elevation="0" outlined class="mb-lg-3 mt-1 pa-6 rounded-lg">
          <v-card-text class="pa-0 mb-1">

            <!-- MEMBERSHIPS FILTERS -->
            <template v-if="revenueTypeFilter === 'subscriptions'">
              <v-row>
                <v-col class="pb-0" cols="12" lg="3">
                  <v-select :items="subscriptionStateFilters" item-text="text" item-value="value"
                    :value="subscriptionsFilter" @input="$emit('update:subscriptions-filter', $event)"
                    :label="$t('message.transactions_admin_plus.status')" dense outlined hide-details
                    clearable></v-select>
                </v-col>
                <v-col class="pb-0" cols="12" lg="3">
                  <v-text-field :hide-details="true" dense outlined
                    :label="$t('message.transactions_admin_plus.filter_by_member')" prepend-inner-icon="mdi-magnify"
                    @keyup="$emit('filter-revenue')" :value="revenueUserFilter"
                    @input="$emit('update:revenue-user-filter', $event)"></v-text-field>
                </v-col>
                <v-col class="pb-0" cols="12" lg="3">
                  <v-autocomplete :items="membershipProducts" item-text="text" item-value="value"
                    :value="revenueProductFilter" @input="$emit('update:revenue-product-filter', $event)"
                    @change="$emit('filter-revenue')" :label="$t('message.transactions_admin_plus.filter_by_product')"
                    dense outlined hide-details clearable></v-autocomplete>
                </v-col>
                <v-col class="pb-0" cols="12" lg="3">
                  <tags-select :value="subscriptionTagFilter" @input="$emit('update:subscription-tag-filter', $event)"
                    @change="$emit('filter-revenue')" :label="$t('message.transactions_admin_plus.filter_table_label')"
                    :items="availableTags"></tags-select>
                </v-col>
              </v-row>
              <v-row class="mt-2">
                <v-col class="pb-0" cols="12" lg="3">
                  <DatePicker @change="$emit('get-recurrent-transactions', true)" :show-time="false" :value="fromFilter"
                    @input="$emit('update:from-filter', $event)" :past-event="true"></DatePicker>
                </v-col>
                <v-col class="pb-0" cols="12" lg="3">
                  <DatePicker @change="$emit('get-recurrent-transactions', true)" :show-time="false" :value="toFilter"
                    @input="$emit('update:to-filter', $event)" :past-event="true"></DatePicker>
                </v-col>
              </v-row>
            </template>

            <!-- ONE-TIME FILTERS -->
            <template v-else-if="revenueTypeFilter === 'onetime'">
              <v-row>
                <v-col class="pb-0" cols="12" lg="4">
                  <v-text-field :hide-details="true" dense outlined
                    :label="$t('message.transactions_admin_plus.filter_by_member')" prepend-inner-icon="mdi-magnify"
                    @keyup="$emit('filter-revenue')" :value="revenueUserFilter"
                    @input="$emit('update:revenue-user-filter', $event)"></v-text-field>
                </v-col>
                <v-col class="pb-0" cols="12" lg="4">
                  <v-autocomplete :items="allProducts" item-text="text" item-value="value" :value="revenueProductFilter"
                    @input="$emit('update:revenue-product-filter', $event)" @change="$emit('filter-revenue')"
                    :label="$t('message.transactions_admin_plus.filter_by_product')" dense outlined hide-details
                    clearable></v-autocomplete>
                </v-col>
                <v-col class="pb-0 d-flex align-center" cols="12" lg="4">
                  <v-switch :input-value="pendingFilter" @change="$emit('update:pending-filter', $event || null)"
                    :label="$t('message.transactions_admin_plus.pending_payment')" hide-details dense
                    color="primary"></v-switch>
                </v-col>
              </v-row>
            </template>

          </v-card-text>
        </v-card>
      </v-container>

      <!-- Table section -->
      <div v-if="!loadingRevenue && revenueFiltered && revenueFiltered.length > 0">
        <v-card-actions class="mr-2">
          <v-row>
            <v-col cols="6" class="d-flex align-center">
              <div class="font-roboto font-weight-medium" style="font-size: 1.25rem">
                {{
                  revenueTypeFilter === 'subscriptions'
                    ? $t('message.transactions_admin_plus.memberships')
                    : $t('message.transactions_admin_plus.one_time_purchases')
                }}
                ({{ revenueFiltered.length }})
              </div>
            </v-col>
            <v-col cols="6" class="font-roboto d-flex justify-end align-center">
              <!-- Trendline toggle – memberships only -->
              <v-btn v-if="revenueTypeFilter === 'subscriptions' && membershipsTrendline && membershipsTrendline.ready"
                outlined text elevation="0" color="primary" small class="mr-2"
                @click="$emit('toggle-memberships-trendline')">
                <v-icon class="mr-1">{{ membershipsTrendlineToggle ? 'mdi-table' : 'mdi-chart-area' }}</v-icon>
                <span v-if="!isSmallerDisplay()">
                  {{ membershipsTrendlineToggle ? $t('message.transactions_admin_plus.table') :
                    $t('message.transactions_admin_plus.graph') }}
                </span>
              </v-btn>
              <!-- Graph/table toggle – one-time with product filter -->
              <v-btn v-if="revenueProductFilter && revenueTypeFilter === 'onetime' && !productsResultByGraph" outlined
                text elevation="0" color="primary" small class="mr-2" @click="$emit('toggle-products-result-by-graph')">
                <v-icon class="mr-1">mdi-chart-line</v-icon>
                <span v-if="!isSmallerDisplay()">{{ $t('message.transactions_admin_plus.graph') }}</span>
              </v-btn>
              <v-btn v-else-if="revenueProductFilter && revenueTypeFilter === 'onetime' && productsResultByGraph"
                outlined text elevation="0" color="primary" small class="mr-2"
                @click="$emit('toggle-products-result-by-graph')">
                <v-icon class="mr-1">mdi-table</v-icon>
                <span v-if="!isSmallerDisplay()">{{ $t('message.transactions_admin_plus.table') }}</span>
              </v-btn>
              <!-- Export CSV -->
              <v-btn v-if="revenueTypeFilter === 'subscriptions'" outlined :disabled="downloadingMembershipsTable" :loading="downloadingMembershipsTable" text
                elevation="0" color="primary" small @click="$emit('download-memberships-table')">
                <span>{{ $t('message.transactions_admin_plus.export_csv') }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>

        <!-- Memberships trendline chart -->
        <div v-show="membershipsTrendlineToggle && revenueTypeFilter === 'subscriptions'">
          <ApexChart type="area" :options="membershipsTrendline.options" height="350"
            :series="membershipsTrendline.data">
          </ApexChart>
        </div>

        <!-- Main data table -->
        <div v-if="!(revenueProductFilter && productsResultByGraph && revenueTypeFilter === 'onetime')" class="mt-2">
          <v-data-table :loading="loadingMembershipsTable || (subscriptionsFilter === 'cancelled' && loadingCancelledSubscriptions)"
            v-show="!membershipsTrendlineToggle || revenueTypeFilter === 'onetime'" item-key="revenueKey"
            :headers="isSmallerDisplay() ? revenueHeadersMobile : revenueHeaders" :items="revenueFiltered"
            :custom-sort="revenueSortMethod" class="tabble-revenue"
            :no-data-text="$t('message.transactions_admin_plus.no_data')" :footer-props="{
              itemsPerPageText: $t('message.transactions_admin_plus.rows_per_page'),
              itemsPerPageOptions: [25, 50, 75],
            }" :hide-default-header="isSmallerDisplay()">
            <template v-slot:item.avatar="{ item }">
              <div class="d-flex align-center text-xs-center clickable pt-3 pb-3" @click.stop="handleRowClick(item)"
                style="gap: 1.5rem">
                <v-avatar size="36"
                  v-if="item.raw.buyer && (item.raw.buyer.userImageSmall || item.raw.buyer.userImage)">
                  <v-img
                    :src="item.raw.buyer.userImageSmall ? item.raw.buyer.userImageSmall : item.raw.buyer.userImage"></v-img>
                </v-avatar>
                <v-avatar size="36" color="plusIndigoLighten3" v-else>
                  <span class="font-roboto white--text text-subtitle-1">{{ getInitials(getDisplayBuyer(item)) }}</span>
                </v-avatar>
              </div>
            </template>

            <template v-slot:item.buyerMobile="{ item }">
              <div class="d-flex flex-column justify-center text-xs-center clickable pt-3 pb-3"
                @click.stop="handleRowClick(item)">
                <span class="clickable font-roboto text-subtitle-1 font-weight-semibold">{{ getDisplayBuyer(item)
                  }}</span>
                <span class="clickable font-roboto text-subtitle-2 font-weight-regular">{{
                  truncate(getDisplayProduct(item), 30, false) }}</span>
              </div>
            </template>

            <template v-slot:item.statusMobile="{ item }">
              <div class="d-flex flex-column justify-center text-xs-center clickable pt-3 pb-3">
                <span class="font-roboto text-subtitle-1 font-weight-semibold">
                  <v-icon v-if="item.raw.coupon && item.raw.coupon.discountedTotal > 0" size="13" color="accent"
                    class="mr-1">mdi-tag-outline</v-icon>
                  {{ currencySymbol }}{{ getAmountForItemProp(item) }}
                </span>
                <div>
                  <v-chip :color="getStatusColorForItem(item).background" small
                    :style="{ color: getStatusColorForItem(item).color }">
                    {{ getStatusTextForItem(item) }}
                  </v-chip>
                </div>
              </div>
            </template>

            <template v-slot:item.buyer="{ item }">
              <div class="d-flex align-center text-xs-center clickable pt-3 pb-3" @click.stop="handleRowClick(item)"
                style="gap: 1.5rem">
                <span class="clickable font-roboto text-subtitle-2 font-weight-regular">{{ getDisplayBuyer(item)
                  }}</span>
              </div>
            </template>

            <template v-slot:item.product="{ item }">
              <span class="clickable font-roboto text-subtitle-2 font-weight-regular"
                @click.stop="handleRowClick(item)">
                <v-tooltip top v-if="item.type === 'subscription' && item.raw.hasNotes">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" size="15" color="accent">mdi-message-text-outline</v-icon>
                  </template>
                  <span>{{ $t('message.transactions_admin_plus.has_notes') }}</span>
                </v-tooltip>
                {{ truncate(getDisplayProduct(item), 30, false) }}
              </span>
            </template>

            <template v-slot:item.frequency="{ item }">
              <span v-if="item.type === 'subscription'" class="font-roboto text-subtitle-2 font-weight-regular">
                <span v-if="item.raw.productInterval == 'month'">{{
                  $t('message.transactions_admin_plus.interval_month').toLowerCase() }}</span>
                <span v-else-if="item.raw.productInterval == 'day'">{{
                  $t('message.transactions_admin_plus.interval_day').toLowerCase() }}</span>
                <span v-else-if="item.raw.productInterval == 'week'">{{
                  $t('message.transactions_admin_plus.interval_week').toLowerCase() }}</span>
                <span v-else-if="item.raw.productInterval == 'bi-week'">{{
                  $t('message.transactions_admin_plus.interval_bi-week').toLowerCase() }}</span>
                <span v-else-if="item.raw.productInterval == 'bi-year'">{{
                  $t('message.transactions_admin_plus.interval_bi-year').toLowerCase() }}</span>
                <span v-else-if="item.raw.productInterval == 'year'">{{
                  $t('message.transactions_admin_plus.interval_year').toLowerCase() }}</span>
                <span v-else-if="item.raw.productInterval == 'once'">{{
                  $t('message.transactions_admin_plus.once').toLowerCase() }}</span>
                <span v-else-if="!isExpiredMembership(item.raw) && getManualExpiryLabel(item.raw)"
                  :class="item.raw.expiringSoon ? 'warning--text' : ''">
                  <v-icon v-if="item.raw.expiringSoon" small color="warning"
                    class="mr-1">mdi-clock-alert-outline</v-icon>
                  {{ getManualExpiryLabel(item.raw) }}
                </span>
                <span v-else-if="!isExpiredMembership(item.raw) && item.raw.expiringSoon" class="warning--text">
                  <v-icon small color="warning" class="mr-1">mdi-clock-alert-outline</v-icon>
                  {{ String($t('message.transactions_admin_plus.expiring_soon')).toLowerCase() }}
                </span>
                <span v-else>{{ $t('message.transactions_admin_plus.manual').toLowerCase() }}</span>
              </span>
            </template>

            <template v-slot:item.date="{ item }">
              <span class="font-roboto text-subtitle-2 font-weight-regular">
                {{ $d(new Date(item.raw.time), 'month_day_year', $i18n.locale) }}
              </span>
            </template>

            <template v-slot:item.billingDay="{ item }">
              <span v-if="item.type === 'subscription'" class="font-roboto text-subtitle-2 font-weight-regular">
                {{ item.raw.displayDate }}
              </span>
            </template>

            <template v-slot:item.price="{ item }">
              <span class="font-roboto text-subtitle-2 font-weight-regular">
                <v-icon v-if="item.raw.coupon && item.raw.coupon.discountedTotal > 0" size="15" color="accent"
                  class="mr-1">mdi-tag-outline</v-icon>
                {{ currencySymbol }}{{ getAmountForItemProp(item) }}
              </span>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColorForItem(item).background" small
                :style="{ color: getStatusColorForItem(item).color }">
                {{ getStatusTextForItem(item) }}
              </v-chip>
            </template>
          </v-data-table>
        </div>

        <!-- One-time product timeline graph -->
        <div v-if="revenueProductFilter && productsResultByGraph && revenueTypeFilter === 'onetime'">
          <v-skeleton-loader v-if="loadingProductTimeline" type="image"></v-skeleton-loader>
          <div v-else-if="productTimelineGraph && productTimelineGraph.options" class="text-center">
            <ApexChart style="margin-top:-30px" type="bar" :options="productTimelineGraph.options" height="200"
              :series="productTimelineGraph.data"></ApexChart>
          </div>
          <v-card-text v-else class="text-center">{{ $t('message.transactions_admin_plus.no_data') }}</v-card-text>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from '@/components/inputs/DateTimePicker.vue';
import TagsSelect from '@/components/inputs/TagsSelect.vue';
import PlusButton from '@/components/plus/PlusButton.vue';
import ApexChart from 'vue-apexcharts';

export default {
  name: 'ViewRevenue',
  components: { DatePicker, TagsSelect, ApexChart, PlusButton },
  props: {
    academy: { type: Object, default: null },
    loadingRevenue: { type: Boolean, default: false },
    stripeReady: { type: Boolean, default: false },
    revenueFiltered: { type: Array, default: () => [] },
    revenueTypeFilter: { type: String, default: 'subscriptions' },
    revenueStatusFilter: { type: String, default: 'all' },
    subscriptionsFilter: { default: null },
    pendingFilter: { type: Boolean, default: false },
    revenueUserFilter: { default: null },
    revenueProductFilter: { default: null },
    subscriptionTagFilter: { default: null },
    allProducts: { type: Array, default: () => [] },
    membershipProducts: { type: Array, default: () => [] },
    availableTags: { type: Array, default: () => [] },
    monthlyRecurringRevenueAmount: { type: [Number, String], default: 0 },
    monthlyTotalRevenue: { type: [Number, String], default: 0 },
    monthlyRecurringRevenue: { type: [Number, String], default: 0 },
    oneTimeRevenueAmount: { type: [Number, String], default: 0 },
    oneTimeRevenueCount: { type: Number, default: 0 },
    pendingPaymentsAmount: { type: [Number, String], default: 0 },
    pendingPaymentsCount: { type: Number, default: 0 },
    fromFilter: { default: null },
    toFilter: { default: null },
    totalCountTitle: { type: String, default: '' },
    totalAmountTitle: { type: String, default: '' },
    totalRevenueAmount: { type: [Number, String], default: 0 },
    totalAmountTooltip: { type: String, default: '' },
    currencySymbol: { type: String, default: '$' },
    productsResultByGraph: { type: Boolean, default: false },
    loadingProductTimeline: { type: Boolean, default: false },
    productTimelineGraph: { default: null },
    downloadingMembershipsTable: { type: Boolean, default: false },
    membershipsTrendline: { type: Object, default: () => ({ ready: false }) },
    membershipsTrendlineToggle: { type: Boolean, default: false },
    loadingMembershipsTable: { type: Boolean, default: false },
    cancelledFiltered: { type: Array, default: () => [] },
    loadingCancelledSubscriptions: { type: Boolean, default: false },
    revenueHeaders: { type: Array, default: () => [] },
    revenueHeadersMobile: { type: Array, default: () => [] },
    getStatusTextForItem: { type: Function, required: true },
    getAmountForItem: { type: Function, required: true },
  },
  data() {
    return {};
  },
  computed: {
    subscriptionStateFilters() {
      if (!this.stripeReady) {
        return [
          { text: this.$t('message.transactions_admin_plus.expiring_soon'), value: 'expiring_soon' },
          { text: this.$t('message.transactions_admin_plus.expired'), value: 'expired' },
          { text: this.$t('message.transactions_admin_plus.inactive_memberships'), value: 'cancelled' },
        ];
      }
      return [
        { text: this.$t('message.transactions_admin_plus.good_standing'), value: 'active' },
        { text: this.$t('message.transactions_admin_plus.past_due'), value: 'past_due' },
        { text: this.$t('message.transactions_admin_plus.waiting_to_start'), value: 'waiting2start' },
        { text: this.$t('message.transactions_admin_plus.pending_payment'), value: 'pending' },
        { text: this.$t('message.transactions_admin_plus.paused'), value: 'paused' },
        { text: this.$t('message.transactions_admin_plus.show_manual_only'), value: 'manual' },
        { text: this.$t('message.transactions_admin_plus.expiring_soon'), value: 'expiring_soon' },
        { text: this.$t('message.transactions_admin_plus.expired'), value: 'expired' },
        { text: this.$t('message.transactions_admin_plus.scheduled_cancellation'), value: 'scheduled_cancellation' },
        { text: this.$t('message.transactions_admin_plus.scheduled_pause'), value: 'scheduled_pause' },
        { text: this.$t('message.transactions_admin_plus.inactive_memberships'), value: 'cancelled' },
      ];
    },
  },
  emits: [
    'filter-revenue',
    'get-recurrent-transactions',
    'download-memberships-table',
    'toggle-memberships-trendline',
    'toggle-products-result-by-graph',
    'get-product-timeline',
    'navigate-to-transaction',
    'go-to-transaction',
    'go-to-member',
    'get-cancelled-subcriptions',
    'navigate-to-revenue-item',
    'update:revenue-type-filter',
    'update:revenue-status-filter',
    'update:subscriptions-filter',
    'update:pending-filter',
    'update:revenue-user-filter',
    'update:revenue-product-filter',
    'update:subscription-tag-filter',
    'update:from-filter',
    'update:to-filter',
  ],
  methods: {
    isExpiredMembership(raw) {
      return (raw.lastPaymentStatus || '').toLowerCase().indexOf('expired') >= 0;
    },
    getManualExpiryLabel(raw) {
      if (raw.subscriptionEndDate) {
        const ms = new Date(raw.subscriptionEndDate).getTime() - Date.now();
        const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
        if (days <= 0) return this.$t('message.transactions_admin_plus.expired');
        return days + (days === 1 ? ' day left' : ' days left');
      }
      if (raw.subscriptionCheckins != null && raw.subscriptionCheckins >= 0) {
        return raw.subscriptionCheckins + ' check-ins left';
      }
      return null;
    },
    getStatusColorForItem(item) {
      const transactionStatusColors = {
        active: { color: '#4CAF50', background: '#E8F5E9' },
        completed: { color: '#4CAF50', background: '#E8F5E9' },
        succeeded: { color: '#4CAF50', background: '#E8F5E9' },
        success: { color: '#4CAF50', background: '#E8F5E9' },
        pending_payment: { color: '#B71C1C', background: '#FFEBEE' },
        expired: { color: '#B71C1C', background: '#FFEBEE' },
        paused: { color: '#FF6F00', background: '#FFF8E1' },
        trialing: { color: '#FF6F00', background: '#FFF8E1' },
        open: { color: '#B71C1C', background: '#FFEBEE' },
        past_due: { color: '#B71C1C', background: '#FFEBEE' },
        pauseDate: { color: '#FF6F00', background: '#FFF8E1' },
        cancellationDate: { color: '#B71C1C', background: '#FFEBEE' },
        default: { color: '#B71C1C', background: '#FFEBEE' },
      };
      const status = this.getStatusTextForItem(item).toLowerCase();
      return transactionStatusColors[status] || transactionStatusColors['default'];
    },
    getDisplayBuyer(item) {
      if (item.raw.buyer) return item.raw.buyer.displayName;
      if (item.raw.email) return item.raw.email;
      return this.$t('message.transactions_admin_plus.pending_onboarding');
    },
    getDisplayProduct(item) {
      if (item.type === 'subscription') {
        return item.raw.productName || (item.raw.product && item.raw.product.name) || '';
      }
      const r = item.raw;
      if (r.product)
        return (r.product.name || '') + (r.product.size ? ' (' + r.product.size + ')' : '');
      if (r.items && r.items[0]) {
        if (r.items.length === 1 && r.items[0].product)
          return r.items[0].product.name + (r.items[0].product.size ? ' (' + r.items[0].product.size + ')' : '');
        if (r.items.length === 1 && r.items[0].name)
          return r.items[0].name + (r.items[0].size ? ' (' + r.items[0].size + ')' : '');
        return r.items.length + ' ' + this.$t('message.transactions_admin_plus.items');
      }
      return '';
    },
    getAmountForItemProp(item) {
      return this.getAmountForItem(item);
    },
    handleRowClick(item) {
      this.$emit('navigate-to-revenue-item', item);
    },
    userImg(src) {
      return this.$emit('user-img', src);
    },
    assets(path) {
      return this.$emit('assets', path);
    },
    revenueSortMethod(items, index, isDesc) {
      if (!items || !index || index.length === 0) return items;
      const idx = index[0];
      const desc = isDesc && isDesc[0];
      const cmp = (a, b) => {
        let aVal, bVal;
        if (idx === 'time') {
          aVal = new Date(a.raw.time || 0).getTime();
          bVal = new Date(b.raw.time || 0).getTime();
          return desc ? bVal - aVal : aVal - bVal;
        }
        if (idx === 'amount') {
          aVal = parseFloat(this.getAmountForItem(a)) || 0;
          bVal = parseFloat(this.getAmountForItem(b)) || 0;
          return desc ? bVal - aVal : aVal - bVal;
        }
        if (idx === 'buyer') {
          aVal = (this.getDisplayBuyer(a) || '').toLowerCase();
          bVal = (this.getDisplayBuyer(b) || '').toLowerCase();
          return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        }
        if (idx === 'product') {
          aVal = (this.getDisplayProduct(a) || '').toLowerCase();
          bVal = (this.getDisplayProduct(b) || '').toLowerCase();
          return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        }
        if (idx === 'status') {
          aVal = (this.getStatusTextForItem(a) || '').toLowerCase();
          bVal = (this.getStatusTextForItem(b) || '').toLowerCase();
          return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        }
        if (idx === 'type') {
          aVal = a.type === 'subscription' ? 0 : 1;
          bVal = b.type === 'subscription' ? 0 : 1;
          return desc ? bVal - aVal : aVal - bVal;
        }
        if (idx === 'frequency') {
          aVal = (a.raw.productInterval || '').toLowerCase();
          bVal = (b.raw.productInterval || '').toLowerCase();
          return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
        }
        if (idx === 'date') {
          aVal = new Date(a.raw.time || 0).getTime();
          bVal = new Date(b.raw.time || 0).getTime();
          return desc ? bVal - aVal : aVal - bVal;
        }
        if (idx === 'billingDay') {
          aVal = new Date(a.raw.displayDate || 0).getTime();
          bVal = new Date(b.raw.displayDate || 0).getTime();
          if (isNaN(aVal) || isNaN(bVal)) {
            const aStr = (a.raw.displayDate || '').toLowerCase();
            const bStr = (b.raw.displayDate || '').toLowerCase();
            return desc ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr);
          }
          return desc ? bVal - aVal : aVal - bVal;
        }
        if (idx === 'price') {
          aVal = parseFloat(this.getAmountForItem(a)) || 0;
          bVal = parseFloat(this.getAmountForItem(b)) || 0;
          return desc ? bVal - aVal : aVal - bVal;
        }
        return 0;
      };
      return [...items].sort(cmp);
    },
  },
};
</script>

<style>
.date-picker {
  font-size: large !important;
  border: 1px solid var(--black-042, rgba(0, 0, 0, 0.42));
  border-radius: 4px;
  display: block;
  width: 100%;
  padding: 0.5rem;
  margin: 0px;
  background-color: #fff;
}

.tabble-revenue .v-data-table-header th,
.tabble-revenue .v-data-table td {
  font-family: 'Roboto', sans-serif !important;
  font-weight: bold !important;
  font-size: 12px !important;
}

@media (max-width: 1264px) {
  .tabble-revenue {
    border: 1px solid #eee;
    padding: 1rem !important;
  }
}

.tabble-revenue .v-data-table__mobile-row__cell {
  text-align: left !important;
}
</style>
